import { fromNullable, none, Option } from 'fp-ts/lib/Option'

import { log } from '@/log'

import { Fku, Sku } from './model/item'

export class ApparelAddToCartSelectionState {
  static create(
    fkuList: Fku[],
    initialSelectedFkuId: string,
    preferredSizePathname: string | null
  ): ApparelAddToCartSelectionState {
    const state = new ApparelAddToCartSelectionState(fkuList)
    state.initializeSelection(initialSelectedFkuId, preferredSizePathname)

    return state
  }

  private _selectedFku: Option<Fku>
  private _selectedSku: Option<Sku>
  private _selectedQuantity: number

  private constructor(
    public readonly fkuList: Fku[]
  ) {
    this._selectedFku = none
    this._selectedSku = none
    this._selectedQuantity = 1
  }

  get selectedFku(): Option<Fku> {
    return this._selectedFku
  }

  get selectedSku(): Option<Sku> {
    return this._selectedSku
  }

  get selectedQuantity(): number {
    return this._selectedQuantity
  }

  get maxSelectableQuantity(): number {
    return this.selectedSku.fold(
      0,
      sku => sku.maxSelectableCount
    )
  }

  selectFku(fkuId: string): void {
    const newFku = fromNullable(this.fkuList.find(f => f.id === fkuId))
    if (newFku.isNone()) {
      log.warn(`requested new fku is not in the list. requested fku id is ${fkuId}`)

      return
    }

    this._selectedFku = newFku
    this.adjustSkuSelection()
  }

  selectSku(skuId: string): void {
    const newSku = this.findSkuInCurrentlySelectedFku(skuId)
    if (newSku.isNone()) {
      log.warn(`requested new sku is not in currently selected fku. requested sku id is ${skuId}`)

      return
    }

    this._selectedSku = newSku

    this.adjustQuantitySelection()
  }

  selectQuantity(count: number): void {
    this._selectedQuantity = count
    this.adjustQuantitySelection()
  }

  private findSkuInCurrentlySelectedFku(skuId: string): Option<Sku> {
    return fromNullable(this.selectedFku.fold(
      null, // FKUが選択されていなければ常にfalse
      fku => fku.skuList.find(s => s.id === skuId)
    ))
  }

  private initializeSelection(initialSelectedFkuId: string, preferredSizePathname: string | null): void {
    this._selectedFku = fromNullable(this.fkuList.find(f => f.id === initialSelectedFkuId)).alt(
      fromNullable(this.fkuList[0] || null)
    )

    this._selectedSku = this.selectedFku.fold<Option<Sku>>(
      none,
      fku =>
        fromNullable(
          fku.skuList.find(s => s.sizePathname === preferredSizePathname)
        ).alt(
          fromNullable(fku.skuList[0] || null)
        )
    )

    this.adjustQuantitySelection()
  }

  /**
   * FKUの変更に追従してSKUを自動選択する
   * - (a) 既存の選択サイズが新たなFKU下に存在する場合はそれを選択する
   * - (b) それ以外の場合は左端のSKUを選択する
   */
  private adjustSkuSelection(): void {
    const currentlySelectedSizePathname = this.selectedSku.map(s => s.sizePathname).toNullable()

    this._selectedSku = this.selectedFku.fold<Option<Sku>>(
      none, // FKUが選択されていなければSKUも選択されない
      fku =>
        fromNullable(
          fku.skuList.find(s => s.sizePathname === currentlySelectedSizePathname)
        ).alt(
          fromNullable(fku.skuList[0] || null)
        )
    )

    this.adjustQuantitySelection()
  }

  /**
   * FKU and/or SKUを変更し、以下の条件に当てはまる場合に選択数量を自動修正する
   * - 新たな選択可能上限数量が既存の選択数量を下回る場合 -> 選択数量を新たな選択可能上限数量に合わせて下方修正する。
   * - 変更前の選択可能上限数量が0で、変更後の選択可能上限数量が1以上の場合 -> 選択数量を0から1に上方修正する
   *
   * ただし、新たな選択可能上限数量が0の場合はなにもしない
   * これは購入不可のSKUを選択したことによって選択中の個数設定がリセットされることを避けるためである
   */
  private adjustQuantitySelection(): void {
    const maxSelectableCount = this.maxSelectableQuantity
    if (maxSelectableCount === 0) {
      return
    }

    if (maxSelectableCount < this.selectedQuantity) {
      this._selectedQuantity = maxSelectableCount

      return
    }

    if (this.selectedQuantity === 0 && (maxSelectableCount >= 1)) {
      this._selectedQuantity = 1

      return
    }
  }
}
